import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganisation } from '../../contexts/OrganisationContext';
import { IDSSelect, IDSButton } from '@inera/ids-react';
import { normalizeOrgSlug, formatRoleName } from '../../utils/tokenUtils';

const OrganisationSelectorDialog = () => {
    const { organisations, activeOrgId, setActiveOrgId, userRoles, activeRoleId, setActiveRoleId, selectorOpen, closeSelector } = useOrganisation();
    const [autoOpen, setAutoOpen] = useState(false);
    const [pendingOrgId, setPendingOrgId] = useState('');
    const [pendingRoleId, setPendingRoleId] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (organisations.length > 0 && !activeOrgId) {
            setAutoOpen(true);
        }
        if (activeOrgId && activeRoleId) {
            setAutoOpen(false);
        }
    }, [organisations, activeOrgId, activeRoleId]);

    // Sync pending state when dialog opens
    useEffect(() => {
        if (autoOpen || selectorOpen) {
            setPendingOrgId(activeOrgId || '');
            setPendingRoleId(activeRoleId || '');
        }
    }, [autoOpen, selectorOpen]);

    const showDialog = autoOpen || selectorOpen;

    const handleOrgChange = (orgId) => {
        setPendingOrgId(orgId || '');
        setPendingRoleId('');
    };

    const handleConfirm = () => {
        setActiveOrgId(pendingOrgId || null);
        setActiveRoleId(pendingRoleId || null);
        setAutoOpen(false);
        closeSelector();
        navigate('/start');
    };

    const handleDismiss = () => {
        closeSelector();
    };

    const filteredRoles = pendingOrgId
        ? userRoles.filter(role => {
            const selectedOrg = organisations.find(org => org.id === pendingOrgId);
            if (!selectedOrg) return false;
            const orgSlug = normalizeOrgSlug(selectedOrg.name);
            return normalizeOrgSlug(role.name).startsWith(orgSlug + '-');
        })
        : [];

    // Guard against a leftover pendingOrgId/pendingRoleId (e.g. restored from a
    // previous selection) that no longer matches anything in the current lists -
    // otherwise Confirm can stay enabled while the dropdowns show no real selection.
    const canConfirm =
        organisations.some(org => org.id === pendingOrgId) &&
        filteredRoles.some(role => role.id === pendingRoleId);

    if (!showDialog) {
        return null;
    }

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '8px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h2 className="ids-heading-m" style={{ color: "var(--ids-palette-primary-40)", fontFamily: '"Poppins"' }}>
                        Välj Organisation och Roll
                    </h2>
                    {selectorOpen && (
                        <button
                            onClick={handleDismiss}
                            aria-label="Stäng"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', lineHeight: 1, padding: '0 0.25rem' }}
                        >
                            ✕
                        </button>
                    )}
                </div>

                <div className="ids-mb-4">
                    {organisations.length === 0 && (
                        <p role="alert" style={{ color: 'var(--ids-palette-error-40, #b30000)', marginBottom: '0.75rem' }}>
                            Inga organisationer kunde hämtas för din inloggning. Prova att logga ut
                            och in igen. Kvarstår problemet, kontakta support.
                        </p>
                    )}
                    <IDSSelect
                        label="Organisation:"
                        id="org-select"
                        value={pendingOrgId}
                        onChange={(e) => handleOrgChange(e.target.value)}
                    >
                        <option value="">Välj en organisation...</option>
                        {organisations.map(org => (
                            <option key={org.id} value={org.id}>
                                {org.displayName ?? org.name}
                            </option>
                        ))}
                    </IDSSelect>
                </div>

                {pendingOrgId && filteredRoles.length > 0 && (
                    <div className="ids-mt-4">
                        <IDSSelect
                            label="Roll:"
                            id="role-select"
                            value={pendingRoleId}
                            onChange={(e) => setPendingRoleId(e.target.value)}
                        >
                            <option value="">Välj en roll...</option>
                            {filteredRoles.map(role => (
                                <option key={role.id} value={role.id}>
                                    {formatRoleName(role.name)}
                                </option>
                            ))}
                        </IDSSelect>
                    </div>
                )}

                <div className="ids-mt-4" style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                    {selectorOpen && (
                        <IDSButton size="s" onClick={handleDismiss}>
                            Avbryt
                        </IDSButton>
                    )}
                    <IDSButton size="s" disabled={!canConfirm} onClick={handleConfirm}>
                        Bekräfta
                    </IDSButton>
                </div>
            </div>
        </div>
    );
};

export default OrganisationSelectorDialog;
